import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Plus, Trash2, MapPin, X, FileText, Download,
  Building2, Globe, Users, ChevronRight, Paperclip,
  ExternalLink, Briefcase, Calendar, DollarSign, Hash,
  Search, Loader2, TrendingUp, TrendingDown, SlidersHorizontal, Check
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const createIcon = (color) => L.divIcon({
  html: `<div style="width:14px;height:14px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 0 8px ${color}88"></div>`,
  className: "", iconSize: [14, 14], iconAnchor: [7, 7],
});

const RATING_COLOR = { green: "#059669", yellow: "#B45309", red: "#DC2626", gray: "#6B7280" };
const RATING_LABEL = { green: "Affidabile", yellow: "Monitorare", red: "A rischio", gray: "Non valutato" };

const createRatedIcon = (typeColor, ratingColor) => L.divIcon({
  html: `<div style="position:relative;width:18px;height:18px;">
    <div style="position:absolute;inset:0;border:2px solid ${ratingColor};border-radius:50%;box-shadow:0 0 10px ${ratingColor}aa;"></div>
    <div style="position:absolute;left:3px;top:3px;width:12px;height:12px;background:${typeColor};border:1.5px solid #fff;border-radius:50%;"></div>
  </div>`,
  className: "", iconSize: [18, 18], iconAnchor: [9, 9],
});

const TYPE_COLOR = {
  competitor: "#DC2626",
  prospect: "#059669",
  client: "#EA580C",
  potential_supplier: "#7C3AED",
  supplier: "#DB2777",
};
const TYPE_ICON = {
  competitor: createIcon(TYPE_COLOR.competitor),
  prospect: createIcon(TYPE_COLOR.prospect),
  client: createIcon(TYPE_COLOR.client),
  potential_supplier: createIcon(TYPE_COLOR.potential_supplier),
  supplier: createIcon(TYPE_COLOR.supplier),
};
const TYPE_ORDER = ["competitor", "prospect", "client", "potential_supplier", "supplier"];
const EMPTY = { name: "", type: "competitor", lat: "", lng: "", city: "", sector: "" };

function ProfileRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-[#050505] border border-white/[0.04]">
      <Icon className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />
      <span className="text-[10px] uppercase tracking-widest text-[#A0AEC0] flex-shrink-0">{label}</span>
      <span className="ml-auto text-xs text-white text-right truncate">{value}</span>
    </div>
  );
}

// ─── Sparkline 6 mesi (rating storico) ─────────────────────────────────────
function RatingSparkline({ history, color }) {
  if (!history || history.length < 2) return null;
  const W = 240, H = 56, PAD = 6;
  const min = Math.min(...history) - 4;
  const max = Math.max(...history) + 4;
  const range = Math.max(1, max - min);
  const points = history.map((v, i) => {
    const x = PAD + (i * (W - PAD * 2)) / (history.length - 1);
    const y = PAD + (H - PAD * 2) * (1 - (v - min) / range);
    return { x, y, v };
  });
  const pathD = points.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaD = `${pathD} L${points[points.length - 1].x.toFixed(1)},${H - PAD} L${points[0].x.toFixed(1)},${H - PAD} Z`;
  const labels = ["-5m", "-4m", "-3m", "-2m", "-1m", "Ora"];
  const last = points[points.length - 1];
  return (
    <div data-testid="rating-sparkline" className="bg-[#050505] border border-white/[0.04] p-3 mb-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] uppercase tracking-widest text-[#A0AEC0]">Trend 6 mesi</span>
        <span className="text-[9px] uppercase tracking-widest text-[#A0AEC0]">
          {history[0]} → <span className="text-white font-semibold tabular-nums">{history[history.length - 1]}</span>
          {history[history.length - 1] > history[0] ? " ↑" : history[history.length - 1] < history[0] ? " ↓" : " →"}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`spark-grad-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.4" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#spark-grad-${color.replace("#", "")})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={i === points.length - 1 ? 3 : 1.8}
            fill={i === points.length - 1 ? color : "#0A0C10"} stroke={color} strokeWidth="1.2" />
        ))}
        <text x={last.x - 10} y={last.y - 8} fontSize="10" fontWeight="700" fill={color} fontFamily="Chivo, monospace">{last.v}</text>
      </svg>
      <div className="flex justify-between mt-1">
        {labels.map((l, i) => (
          <span key={i} className="text-[8px] uppercase tracking-wider text-[#6B7280]">{l}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Competitor Detail Panel ────────────────────────────────────────────────
function CompetitorPanel({ marker, onClose, onUpdate }) {
  const { t } = useLanguage();
  const [detail, setDetail] = useState(null);
  const [docs, setDocs] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ markets: "", clients: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadDetail();
    loadDocs();
  }, [marker.id]);

  const loadDetail = async () => {
    try {
      const { data } = await axios.get(`${API}/warroom/markers/${marker.id}/detail`, { withCredentials: true });
      setDetail(data);
      // Clients può essere [str] (legacy) o [{name, website}] (nuovo) — normalizziamo in stringa per il form
      const clientsAsStr = (data.clients || []).map(c => typeof c === "string" ? c : c.name).join(", ");
      setForm({
        markets: (data.markets || []).join(", "),
        clients: clientsAsStr,
        description: data.description || "",
      });
    } catch {
      setDetail({ markets: [], clients: [], description: "", profile: null });
    }
  };

  const loadDocs = async () => {
    try {
      const { data } = await axios.get(`${API}/warroom/markers/${marker.id}/documents`, { withCredentials: true });
      setDocs(data);
    } catch { setDocs([]); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Mantieni i website già noti quando l'admin riedita la lista clienti
      const existingClientsMap = new Map(
        (detail?.clients || [])
          .map(c => typeof c === "string" ? [c, ""] : [c.name, c.website || ""])
      );
      const newClients = form.clients.split(",")
        .map(s => s.trim()).filter(Boolean)
        .map(name => ({ name, website: existingClientsMap.get(name) || "" }));
      const payload = {
        markets: form.markets.split(",").map(s => s.trim()).filter(Boolean),
        clients: newClients,
        description: form.description,
        // Conserva il profilo esistente (non editato qui)
        profile: detail?.profile || null,
      };
      await axios.put(`${API}/warroom/markers/${marker.id}/detail`, payload, { withCredentials: true });
      await loadDetail();
      setEditMode(false);
      onUpdate && onUpdate();
    } finally { setSaving(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/warroom/documents/${doc.id}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.content_type || "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const accentColor = TYPE_COLOR[marker.type] || TYPE_COLOR.competitor;
  const typeLabel = t(`warroom.${marker.type || "competitor"}`);

  return (
    <div data-testid="competitor-panel" className="fixed inset-0 flex" style={{ zIndex: 9999 }}>
      {/* Backdrop */}
      <div className="flex-1 detail-backdrop-solid" onClick={onClose} />

      {/* Slide-over panel */}
      <div className="detail-panel-light h-full w-full max-w-[640px] border-l-2 flex flex-col overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.6)]"
        style={{ borderLeftColor: accentColor }}>
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/[0.04] bg-[#0D0F12]">
          <div className="w-11 h-11 flex items-center justify-center border-2 flex-shrink-0"
            style={{ borderColor: accentColor, backgroundColor: `${accentColor}1A` }}>
            <Building2 className="w-5 h-5" style={{ color: accentColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{marker.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] px-2 py-0.5 border font-bold tracking-widest uppercase"
                style={{ color: accentColor, borderColor: accentColor, backgroundColor: `${accentColor}15` }}>
                {typeLabel}
              </span>
              {marker.city && <span className="text-xs text-[#CBD5E1]">{marker.city}</span>}
              {marker.sector && <span className="text-xs text-[#A0AEC0]">· {marker.sector}</span>}
            </div>
          </div>
          <button data-testid="panel-close" onClick={onClose}
            className="text-[#CBD5E1] hover:text-white hover:bg-[#1F242D] p-2 transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Profilo aziendale */}
          {detail?.profile && (
            <section className="bg-[#0D0F12] border border-white/[0.04] p-5" data-testid="profile-section">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-[#00E5FF]" />
                <p className="text-[11px] text-[#CBD5E1] tracking-[0.15em] uppercase font-semibold">{t("warroom.profile_title")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {detail.profile.sector && (
                  <ProfileRow icon={Briefcase} label={t("warroom.field_sector")} value={detail.profile.sector} />
                )}
                {detail.profile.founded && (
                  <ProfileRow icon={Calendar} label={t("warroom.field_founded")} value={detail.profile.founded} />
                )}
                {detail.profile.employees && (
                  <ProfileRow icon={Users} label={t("warroom.field_employees")} value={detail.profile.employees.toLocaleString("it-IT")} />
                )}
                {detail.profile.revenue && (
                  <ProfileRow icon={DollarSign} label={t("warroom.field_revenue")} value={detail.profile.revenue} />
                )}
                {detail.profile.hq && (
                  <ProfileRow icon={MapPin} label={t("warroom.field_hq")} value={detail.profile.hq} />
                )}
                {detail.profile.website && (
                  <div className="col-span-2 flex items-center gap-2 px-3 py-2 bg-[#050505] border border-white/[0.04] hover:border-[#00E5FF]/40 transition-colors">
                    <Globe className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />
                    <span className="text-[10px] uppercase tracking-widest text-[#A0AEC0]">{t("warroom.field_website")}</span>
                    <a href={detail.profile.website} target="_blank" rel="noopener noreferrer"
                      data-testid="profile-website-link"
                      className="ml-auto text-xs text-[#00E5FF] hover:underline truncate flex items-center gap-1">
                      {detail.profile.website.replace(/^https?:\/\//, "")}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Supplier Rating (semaforo + KPI) */}
          {detail?.rating && (marker.type === "supplier" || marker.type === "potential_supplier") && (
            <section data-testid="supplier-rating-section"
              className="bg-[#0D0F12] border p-5"
              style={{ borderColor: `${RATING_COLOR[detail.rating.overall] || RATING_COLOR.gray}55` }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{
                    background: RATING_COLOR[detail.rating.overall] || RATING_COLOR.gray,
                    boxShadow: `0 0 10px ${RATING_COLOR[detail.rating.overall] || RATING_COLOR.gray}`,
                  }} />
                  <p className="text-[11px] tracking-[0.15em] uppercase font-semibold" style={{ color: RATING_COLOR[detail.rating.overall] || RATING_COLOR.gray }}>
                    Rating Fornitore · {RATING_LABEL[detail.rating.overall] || RATING_LABEL.gray}
                  </p>
                </div>
                {typeof detail.rating.score === "number" && (
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold tabular-nums" style={{ color: RATING_COLOR[detail.rating.overall] || RATING_COLOR.gray, fontFamily: "Chivo, sans-serif" }}>
                      {detail.rating.score}
                    </span>
                    <span className="text-[10px] text-[#A0AEC0]">/ 100</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-[#050505] border border-white/[0.04] p-3">
                  <p className="text-[9px] uppercase tracking-widest text-[#A0AEC0] mb-1">SLA</p>
                  <p className="text-sm text-white font-bold tabular-nums">{detail.rating.sla_pct ?? "—"}%</p>
                </div>
                <div className="bg-[#050505] border border-white/[0.04] p-3">
                  <p className="text-[9px] uppercase tracking-widest text-[#A0AEC0] mb-1">Qualità</p>
                  <p className="text-sm text-white font-bold tabular-nums">{detail.rating.quality_pct ?? "—"}%</p>
                </div>
                <div className="bg-[#050505] border border-white/[0.04] p-3">
                  <p className="text-[9px] uppercase tracking-widest text-[#A0AEC0] mb-1">Lead Time</p>
                  <p className="text-sm text-white font-bold tabular-nums">{detail.rating.lead_time_days ?? "—"}gg</p>
                </div>
              </div>
              <RatingSparkline history={detail.rating.score_history} color={RATING_COLOR[detail.rating.overall] || RATING_COLOR.gray} />
              {detail.rating.notes && (
                <p className="text-[11px] text-[#CBD5E1] leading-relaxed italic">{detail.rating.notes}</p>
              )}
            </section>
          )}
          <section className="bg-[#0D0F12] border border-white/[0.04] p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] text-[#CBD5E1] tracking-[0.15em] uppercase font-semibold">{t("warroom.profile")}</p>
              <button onClick={() => setEditMode(!editMode)}
                className="text-[11px] text-[#00E5FF] hover:underline font-medium">
                {editMode ? t("action.cancel") : t("action.edit")}
              </button>
            </div>
            {editMode ? (
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={4} placeholder={t("warroom.description_placeholder")}
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] resize-none leading-relaxed"
                style={{ fontFamily: "JetBrains Mono, monospace" }} />
            ) : (
              <p className="text-sm text-[#D1D5DB] leading-relaxed whitespace-pre-wrap">
                {detail?.description || <span className="text-[#A0AEC0] italic">{t("warroom.no_description")}</span>}
              </p>
            )}
          </section>

          {/* Markets + Clients grid */}
          <div className="grid grid-cols-1 gap-5">
            <section className="bg-[#0D0F12] border border-white/[0.04] p-5">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-[#00E5FF]" />
                <p className="text-[11px] text-[#CBD5E1] tracking-[0.15em] uppercase font-semibold">{t("warroom.markets_present")}</p>
              </div>
              {editMode ? (
                <input value={form.markets} onChange={e => setForm(p => ({ ...p, markets: e.target.value }))}
                  placeholder={t("warroom.markets_placeholder")}
                  className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]" />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(detail?.markets || []).length > 0
                    ? detail.markets.map((m, i) => (
                      <span key={`${m}-${i}`} className="px-2.5 py-1 bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-xs text-[#00E5FF] font-medium">{m}</span>
                    ))
                    : <span className="text-xs text-[#A0AEC0] italic">{t("warroom.no_markets")}</span>
                  }
                </div>
              )}
            </section>

            <section className="bg-[#0D0F12] border border-white/[0.04] p-5">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-[#A78BFA]" />
                <p className="text-[11px] text-[#CBD5E1] tracking-[0.15em] uppercase font-semibold">{t("warroom.known_clients")}</p>
              </div>
              {editMode ? (
                <input value={form.clients} onChange={e => setForm(p => ({ ...p, clients: e.target.value }))}
                  placeholder={t("warroom.clients_placeholder")}
                  className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]" />
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="known-clients-list">
                  {(detail?.clients || []).length > 0
                    ? detail.clients.map((c, i) => {
                        const name = typeof c === "string" ? c : c.name;
                        const website = typeof c === "string" ? null : c.website;
                        if (website) {
                          return (
                            <a key={`${name}-${i}`} href={website} target="_blank" rel="noopener noreferrer"
                              data-testid="known-client-link"
                              className="group flex items-center gap-1.5 px-2.5 py-1 bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-xs text-[#A78BFA] font-medium hover:bg-[#A78BFA]/20 hover:border-[#A78BFA] transition-colors">
                              {name}
                              <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                            </a>
                          );
                        }
                        return (
                          <span key={`${name}-${i}`} className="px-2.5 py-1 bg-[#A78BFA]/10 border border-[#A78BFA]/30 text-xs text-[#A78BFA] font-medium">{name}</span>
                        );
                      })
                    : <span className="text-xs text-[#A0AEC0] italic">{t("warroom.no_clients")}</span>
                  }
                </div>
              )}
            </section>
          </div>

          {editMode && (
            <button data-testid="panel-save" onClick={handleSave} disabled={saving}
              className="w-full py-2.5 bg-[#00E5FF] text-black text-sm font-bold hover:bg-[#00B3CC] transition-colors disabled:opacity-50 tracking-wider uppercase">
              {saving ? t("common.saving") : t("warroom.save_changes")}
            </button>
          )}

          {/* Documents */}
          <section className="bg-[#0D0F12] border border-white/[0.04] p-5">
            <div className="flex items-center gap-2 mb-2">
              <Paperclip className="w-4 h-4 text-[#FF9F0A]" />
              <p className="text-[11px] text-[#CBD5E1] tracking-[0.15em] uppercase font-semibold">{t("warroom.documents")} ({docs.length})</p>
            </div>
            <p className="text-[11px] text-[#A0AEC0] mb-4">{t("warroom.documents_info")}</p>

            {docs.length === 0 ? (
              <div className="border border-dashed border-white/[0.04] p-6 text-center">
                <FileText className="w-5 h-5 text-[#A0AEC0] mx-auto mb-2" />
                <p className="text-xs text-[#A0AEC0]">{t("warroom.no_documents")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {docs.map(doc => (
                  <button key={doc.id} data-testid="doc-row" onClick={() => handleDownload(doc)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-[#050505] border border-white/[0.04] hover:border-[#FF9F0A]/50 hover:bg-[#FF9F0A]/5 transition-all text-left group">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 flex-shrink-0">
                      <FileText className="w-4 h-4 text-[#FF9F0A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">{doc.filename}</p>
                      <p className="text-[11px] text-[#A0AEC0] mt-0.5">
                        {doc.size_kb} KB · PDF · {doc.uploaded_at?.slice(0, 10)}
                      </p>
                    </div>
                    <Download className="w-4 h-4 text-[#A0AEC0] group-hover:text-[#FF9F0A] transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ─── Main War Room ───────────────────────────────────────────────────────────
export default function WarRoom() {
  const { t } = useLanguage();
  const [markers, setMarkers] = useState([]);
  const [alerts, setAlerts] = useState({ declining: [], improving: [] });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState(new Set()); // empty = tutti
  const [ratingFilter, setRatingFilter] = useState(new Set()); // empty = tutti
  const [selectedMarker, setSelectedMarker] = useState(null);
  // Geocoding state (Nominatim via /api/warroom/geocode)
  const [geoQ, setGeoQ] = useState("");
  const [geoResults, setGeoResults] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, alertsRes] = await Promise.all([
        axios.get(`${API}/warroom/markers`, { withCredentials: true }),
        axios.get(`${API}/dashboard/supplier-alerts`, { withCredentials: true }).catch(() => ({ data: { declining: [], improving: [] } })),
      ]);
      setMarkers(data);
      setAlerts(alertsRes.data || { declining: [], improving: [] });
    } finally { setLoading(false); }
  };

  // Map marker_id → 'up' (improving) | 'down' (declining) for the blinking chip
  const trendById = useMemo(() => {
    const m = new Map();
    (alerts.improving || []).forEach(a => m.set(a.marker_id, "up"));
    (alerts.declining || []).forEach(a => m.set(a.marker_id, "down"));
    return m;
  }, [alerts]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng) };
      await axios.post(`${API}/warroom/markers`, payload, { withCredentials: true });
      setForm(EMPTY);
      setGeoQ(""); setGeoResults([]); setGeoError("");
      setShowAdd(false);
      await load();
    } finally { setSaving(false); }
  };

  const doGeocode = async () => {
    const q = geoQ.trim();
    if (q.length < 2) { setGeoError("Inserisci almeno 2 caratteri"); return; }
    setGeoLoading(true); setGeoError(""); setGeoResults([]);
    try {
      const { data } = await axios.get(`${API}/warroom/geocode`, { params: { q }, withCredentials: true });
      const results = data.results || [];
      if (results.length === 0) { setGeoError("Nessun risultato trovato"); return; }
      setGeoResults(results);
      // Auto-apply the first result to the form for convenience
      const first = results[0];
      setForm(p => ({
        ...p,
        lat: String(first.lat),
        lng: String(first.lng),
        city: first.city || p.city,
      }));
    } catch (err) {
      setGeoError(err.response?.data?.detail || "Errore geocoding");
    } finally { setGeoLoading(false); }
  };

  const applyGeoResult = (r) => {
    setForm(p => ({ ...p, lat: String(r.lat), lng: String(r.lng), city: r.city || p.city }));
  };

  const handleDelete = async (id) => {
    if (selectedMarker?.id === id) setSelectedMarker(null);
    await axios.delete(`${API}/warroom/markers/${id}`, { withCredentials: true });
    await load();
  };

  const showRatingFilter = filter.size === 0 || filter.has("supplier") || filter.has("potential_supplier");
  const filtered = useMemo(() => {
    let out = filter.size === 0 ? markers : markers.filter(m => filter.has(m.type));
    if (ratingFilter.size > 0) {
      out = out.filter(m => {
        // Applica il filtro rating solo su fornitori/potenziali fornitori
        if (m.type !== "supplier" && m.type !== "potential_supplier") return false;
        return ratingFilter.has(m.rating_overall);
      });
    }
    return out;
  }, [filter, markers, ratingFilter]);
  const byType = useMemo(() => ({
    competitor: markers.filter(m => m.type === "competitor"),
    prospect: markers.filter(m => m.type === "prospect"),
    client: markers.filter(m => m.type === "client"),
    potential_supplier: markers.filter(m => m.type === "potential_supplier"),
    supplier: markers.filter(m => m.type === "supplier"),
  }), [markers]);
  const counts = {
    competitor: byType.competitor.length,
    prospect: byType.prospect.length,
    client: byType.client.length,
    potential_supplier: byType.potential_supplier.length,
    supplier: byType.supplier.length,
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 pr-24 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{t("warroom.title")}</h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">{t("warroom.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="add-marker-button" onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-2 bg-[#1F242D] text-white border border-white/[0.04] px-3 py-2 text-xs hover:bg-[#30363D] transition-colors rounded">
            <Plus className="w-3.5 h-3.5" /> {t("warroom.add_marker")}
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <form onSubmit={handleAdd} className="bg-[#0D0F12] border-b border-white/[0.04] px-6 py-4 space-y-3">
          {/* Geocoding row */}
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">
                <MapPin className="w-3 h-3 inline mr-1" /> Ricerca indirizzo (Nominatim OSM)
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B7280]" />
                <input
                  data-testid="warroom-geocode-input"
                  value={geoQ} onChange={e => setGeoQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doGeocode(); } }}
                  placeholder="Es. Via Roma 12, Milano  ·  oppure  ·  Frankfurt Germany"
                  className="w-full bg-[#050505] border border-white/[0.04] text-white pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF] placeholder:text-[#6B7280]"
                />
              </div>
            </div>
            <button
              type="button"
              data-testid="warroom-geocode-btn"
              onClick={doGeocode}
              disabled={geoLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#1F242D] text-white border border-white/[0.04] hover:bg-[#30363D] transition-colors disabled:opacity-50"
            >
              {geoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              {geoLoading ? "Cerco…" : "Trova coordinate"}
            </button>
          </div>
          {geoError && (
            <div data-testid="warroom-geocode-error" className="text-xs text-[#FF3B30] flex items-center gap-1">
              <X className="w-3 h-3" /> {geoError}
            </div>
          )}
          {geoResults.length > 0 && (
            <div data-testid="warroom-geocode-results" className="border border-white/[0.04] bg-[#050505]">
              <div className="px-3 py-1.5 border-b border-white/[0.04] text-[9px] tracking-widest uppercase text-[#A0AEC0] flex items-center justify-between">
                <span>{geoResults.length} risultati · click per usare</span>
                <button type="button" onClick={() => setGeoResults([])} className="text-[#6B7280] hover:text-white"><X className="w-3 h-3" /></button>
              </div>
              <div className="max-h-32 overflow-y-auto">
                {geoResults.map((r, i) => (
                  <button
                    key={`${r.lat}-${r.lng}-${i}`} type="button"
                    data-testid={`warroom-geocode-result-${i}`}
                    onClick={() => applyGeoResult(r)}
                    className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-[#12161C] border-b border-white/[0.04] last:border-b-0"
                  >
                    <MapPin className="w-3 h-3 text-[#00E5FF] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{r.display_name}</p>
                      <p className="text-[10px] text-[#6B7280] tabular-nums">
                        {r.lat.toFixed(4)}, {r.lng.toFixed(4)}
                        {r.city && <span className="ml-2 text-[#00E5FF]">· {r.city}</span>}
                        {r.country && <span className="ml-1 text-[#A0AEC0]">· {r.country}</span>}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            {[["name", t("warroom.marker_name") + " *"], ["lat", "Lat *"], ["lng", "Lng *"], ["city", t("warroom.city")], ["sector", t("warroom.sector")]].map(([f, l]) => (
              <div key={f}>
                <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">{l}</label>
                <input value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} required={l.includes("*")}
                  className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]" />
              </div>
            ))}
            <div>
              <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1">{t("warroom.type")}</label>
              <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#00E5FF]">
                <option value="competitor">{t("warroom.competitor")}</option>
                <option value="prospect">{t("warroom.prospect")}</option>
                <option value="client">{t("warroom.client")}</option>
                <option value="potential_supplier">{t("warroom.potential_supplier")}</option>
                <option value="supplier">{t("warroom.supplier")}</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs bg-[#1F242D] text-white border border-white/[0.04] hover:bg-[#30363D]">{t("action.cancel")}</button>
              <button data-testid="save-marker-button" type="submit" disabled={saving}
                className="px-3 py-1.5 text-xs bg-[#00E5FF] text-black font-semibold hover:bg-[#00B3CC] disabled:opacity-50">{saving ? "..." : t("action.save")}</button>
            </div>
          </div>
        </form>
      )}

      {/* Map + List */}
      <div className="flex-1 flex overflow-hidden gap-4 p-4 bg-[#050709]">
        <div className="flex-1 relative rounded-2xl overflow-hidden shadow-xl" style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.4)" }}>
          {!loading && (
            <MapContainer center={[48, 15]} zoom={4} style={{ width: "100%", height: "100%" }} zoomControl={true}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap contributors &copy; CARTO' />
              {filtered.map(m => {
                const isSupply = m.type === "supplier" || m.type === "potential_supplier";
                const rOverall = m.rating_overall;
                const icon = isSupply && rOverall
                  ? createRatedIcon(TYPE_COLOR[m.type] || TYPE_COLOR.competitor, RATING_COLOR[rOverall] || RATING_COLOR.gray)
                  : (TYPE_ICON[m.type] || TYPE_ICON.competitor);
                return (
                <Marker key={m.id} position={[m.lat, m.lng]} icon={icon}>
                  <Popup>
                    <div className="bg-[#0D0F12] border border-white/[0.04] p-2 min-w-[160px]">
                      <p className="text-white font-semibold text-sm">{m.name}</p>
                      <p className="text-xs mb-2" style={{ color: TYPE_COLOR[m.type] || TYPE_COLOR.competitor }}>{t(`warroom.${m.type || "competitor"}`)}</p>
                      {m.city && <p className="text-xs text-[#CBD5E1]">{m.city}</p>}
                      {m.sector && <p className="text-xs text-[#CBD5E1]">{m.sector}</p>}
                      {isSupply && rOverall && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className="inline-block w-2 h-2 rounded-full" style={{ background: RATING_COLOR[rOverall] }} />
                          <span className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: RATING_COLOR[rOverall] }}>
                            {RATING_LABEL[rOverall]}{typeof m.rating_score === "number" ? ` · ${m.rating_score}` : ""}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => setSelectedMarker(m)}
                        className="mt-2 w-full flex items-center justify-center gap-1 px-2 py-1 bg-[#1A1D24] border border-white/[0.04] text-xs text-white hover:bg-[#30363D] transition-colors"
                      >
                        <ChevronRight className="w-3 h-3" /> {t("action.open")}
                      </button>
                    </div>
                  </Popup>
                </Marker>
                );
              })}
            </MapContainer>
          )}
        </div>

        {/* Sidebar list */}
        <div className="w-80 flex flex-col gap-3 overflow-hidden">
          {/* Card Panoramica */}
          <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }}>
            <div className="px-4 py-2.5 flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-[#00E5FF]" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#00E5FF] font-bold">Panoramica</p>
              <span className="ml-auto text-[10px] text-[#6B7280] tabular-nums">
                {markers.length} totali
              </span>
            </div>
            <div className="px-3 pb-3 flex flex-col">
              {TYPE_ORDER.map(tp => (
                <div
                  key={tp}
                  data-testid={`overview-tile-${tp}`}
                  className="flex items-center gap-3 px-2 py-2 hover:bg-[#12161C] transition-colors rounded-md"
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: TYPE_COLOR[tp], boxShadow: `0 0 6px ${TYPE_COLOR[tp]}` }}
                  />
                  <p className="text-sm font-bold text-white tabular-nums leading-none flex-shrink-0 w-8 text-right" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {counts[tp]}
                  </p>
                  <p className="text-[10px] tracking-[0.14em] uppercase text-[#CBD5E1] truncate">
                    {t(`warroom.${tp}s`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Card Filtri (Tipologia + Stato di salute) - Menu a tendina */}
          <div className="bg-[#0D0F12] border border-white/[0.06] rounded-xl shadow-lg">
            <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center gap-2">
              <SlidersHorizontal className="w-3.5 h-3.5 text-[#00E5FF]" />
              <p className="text-[11px] tracking-[0.2em] uppercase text-white font-bold">Filtri</p>
              {(filter.size > 0 || ratingFilter.size > 0) && (
                <button
                  data-testid="filters-reset"
                  onClick={() => { setFilter(new Set()); setRatingFilter(new Set()); }}
                  className="ml-auto text-[9px] tracking-wider uppercase text-[#00E5FF] hover:text-white"
                >Reset</button>
              )}
            </div>

            {/* Dropdown Tipologia */}
            <details data-testid="dropdown-tipologia" className="warroom-dropdown group border-b border-white/[0.06]">
              <summary className="list-none cursor-pointer px-4 py-2.5 flex items-center gap-2 hover:bg-[#12161C] transition-colors">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#CBD5E1] font-semibold">Tipologia</p>
                <span className="ml-auto flex items-center gap-1.5">
                  <span className="text-[10px] text-white font-semibold">
                    {filter.size === 0 ? "Tutti" : `${filter.size} selez.`}
                  </span>
                  <ChevronRight className="w-3 h-3 text-[#8B9AB0] transition-transform group-open:rotate-90" />
                </span>
              </summary>
              <div className="px-2 pb-3 pt-1 flex flex-col gap-1">
                {["competitor", "prospect", "client", "potential_supplier", "supplier"].map(f => {
                  const active = filter.has(f);
                  const color = TYPE_COLOR[f];
                  return (
                    <button
                      key={f}
                      data-testid={`dropdown-type-${f}`}
                      onClick={() => setFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(f)) next.delete(f); else next.add(f);
                        return next;
                      })}
                      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-[11px] rounded-md transition-colors ${active ? "bg-[#12161C]" : "hover:bg-[#12161C]"}`}
                    >
                      <span
                        className="w-4 h-4 rounded-[3px] flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{
                          backgroundColor: active ? color : "transparent",
                          border: `1.5px solid ${active ? color : "#3A4152"}`,
                        }}
                      >
                        {active && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                      </span>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color, boxShadow: `0 0 4px ${color}` }} />
                      <span className={`flex-1 text-left tracking-wider uppercase ${active ? "text-white font-semibold" : "text-[#CBD5E1]"}`}>
                        {t(`warroom.${f}`)}
                      </span>
                      <span className="text-[10px] text-[#6B7280] tabular-nums font-mono">{counts[f]}</span>
                    </button>
                  );
                })}
              </div>
            </details>

            {/* Dropdown Stato di salute */}
            <details data-testid="dropdown-salute" className="warroom-dropdown group">
              <summary className="list-none cursor-pointer px-4 py-2.5 flex items-center gap-2 hover:bg-[#12161C] transition-colors">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#CBD5E1] font-semibold">Stato di salute</p>
                <span className="ml-auto flex items-center gap-1.5">
                  <span className="text-[10px] text-white font-semibold">
                    {ratingFilter.size === 0 ? "Tutti" : `${ratingFilter.size} selez.`}
                  </span>
                  <ChevronRight className="w-3 h-3 text-[#8B9AB0] transition-transform group-open:rotate-90" />
                </span>
              </summary>
              <div className="px-2 pb-3 pt-1 flex flex-col gap-1">
                {[
                  { k: "green", color: RATING_COLOR.green, label: "Affidabile" },
                  { k: "yellow", color: RATING_COLOR.yellow, label: "Monitorare" },
                  { k: "red", color: RATING_COLOR.red, label: "A rischio" },
                ].map(opt => {
                  const active = ratingFilter.has(opt.k);
                  return (
                    <button
                      key={opt.k}
                      data-testid={`dropdown-rating-${opt.k}`}
                      onClick={() => setRatingFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(opt.k)) next.delete(opt.k); else next.add(opt.k);
                        return next;
                      })}
                      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-[11px] rounded-md transition-colors ${active ? "bg-[#12161C]" : "hover:bg-[#12161C]"}`}
                    >
                      <span
                        className="w-4 h-4 rounded-[3px] flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{
                          backgroundColor: active ? opt.color : "transparent",
                          border: `1.5px solid ${active ? opt.color : "#3A4152"}`,
                        }}
                      >
                        {active && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                      </span>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: opt.color, boxShadow: `0 0 4px ${opt.color}` }} />
                      <span className={`flex-1 text-left tracking-wider uppercase ${active ? "text-white font-semibold" : "text-[#CBD5E1]"}`}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </details>
          </div>

          {/* Card Lista aziende */}
          <div className="bg-[#0D0F12] border border-white/[0.06] rounded-xl shadow-lg flex-1 overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 border-b border-white/[0.06] flex-shrink-0">
              <p className="text-[11px] tracking-[0.2em] uppercase text-white font-bold">Aziende <span className="text-[#6B7280] font-normal">· {filtered.length}</span></p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {(filter.size === 0 ? TYPE_ORDER : TYPE_ORDER.filter(tp => filter.has(tp))).map(type => {
                const items = byType[type].filter(m => {
                  if (ratingFilter.size === 0) return true;
                  if (type !== "supplier" && type !== "potential_supplier") return false;
                  return ratingFilter.has(m.rating_overall);
                });
                if (items.length === 0) return null;
                return (
                  <div key={type}>
                    <div className="px-4 py-1.5 border-b border-white/[0.04] bg-[#0A0C10]/50">
                      <p className="text-[9px] tracking-[0.22em] uppercase font-semibold" style={{ color: TYPE_COLOR[type] }}>{t(`warroom.${type}s`)}</p>
                    </div>
                    {items.map(m => {
                      const showRating = (type === "supplier" || type === "potential_supplier") && m.rating_overall;
                      const trend = trendById.get(m.id);
                      return (
                        <div key={m.id} data-testid="map-marker-row"
                          className="flex items-start gap-3 px-4 py-3 border-b border-white/[0.04] hover:bg-[#1A1D24] transition-colors group cursor-pointer"
                          onClick={() => setSelectedMarker(m)}>
                          <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: TYPE_COLOR[type] }} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-xs text-white font-medium truncate">{m.name}</p>
                              {showRating && (
                                <span data-testid="sidebar-rating-dot" title={`${RATING_LABEL[m.rating_overall]} · ${m.rating_score ?? ""}`}
                                  className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: RATING_COLOR[m.rating_overall], boxShadow: `0 0 6px ${RATING_COLOR[m.rating_overall]}` }} />
                              )}
                              {trend && (
                                <span
                                  data-testid={`trend-chip-${trend}`}
                                  title={trend === "up" ? "Fornitore in miglioramento (ultimi 3 mesi)" : "Fornitore in peggioramento (ultimi 3 mesi)"}
                                  className={`inline-flex items-center gap-0.5 px-1 py-0.5 border rounded-sm text-[8px] font-bold tracking-widest uppercase flex-shrink-0 warroom-trend-blink ${
                                    trend === "up" ? "border-[#059669] bg-[#059669]/15 text-[#059669]" : "border-[#DC2626] bg-[#DC2626]/15 text-[#DC2626]"
                                  }`}
                                >
                                  {trend === "up" ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-[#A0AEC0]">{m.city}{m.sector ? ` · ${m.sector}` : ""}</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <ChevronRight className="w-3.5 h-3.5 text-[#A0AEC0] opacity-0 group-hover:opacity-100 transition-all" />
                            <button onClick={e => { e.stopPropagation(); handleDelete(m.id); }}
                              className="text-[#A0AEC0] hover:text-[#FF3B30] transition-all opacity-0 group-hover:opacity-100">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedMarker && (
        <CompetitorPanel
          marker={selectedMarker}
          onClose={() => setSelectedMarker(null)}
          onUpdate={load}
        />
      )}
    </div>
  );
}
